import React, { useState, useRef } from 'react';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectUser, selectUserProfile, selectPermissions, setAuthData } from '@/store/slices/authSlice';
import { updateProfile, signOut } from 'firebase/auth';
import { storage, auth, db } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { Upload, Loader2, Cookie, LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import { ThemeToggle } from '@/components/ThemeToggle';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ProfileSheetProps {
  isOpen: boolean;
  onClose: () => void;
  side?: 'top' | 'bottom' | 'left' | 'right' | undefined;
}

async function dataURLtoFile(dataurl: string, filename: string): Promise<File | null> {
  const arr = dataurl.split(',');
  if (arr.length < 2) return null;
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) return null;
  const mime = mimeMatch[1];
  try {
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while(n--){
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type:mime});
  } catch (e) {
    console.error("Error converting data URL to file:", e);
    return null;
  }
}

const ProfileSheet: React.FC<ProfileSheetProps> = ({ isOpen, onClose, side }) => {
  const currentUser = useAppSelector(selectUser);
  const userProfile = useAppSelector(selectUserProfile);
  const permissions = useAppSelector(selectPermissions);
  const dispatch = useAppDispatch();
  
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [croppedImagePreviewUrl, setCroppedImagePreviewUrl] = useState<string | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  if (!currentUser) return null;

  const getInitials = () => {
    if (userProfile?.username) return userProfile.username.slice(0, 2).toUpperCase();
    if (currentUser.displayName) {
      const parts = currentUser.displayName.split(' ');
      return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : parts[0]?.[0]?.toUpperCase() || '?';
    }
    return currentUser.email?.[0]?.toUpperCase() || '?';
  };

  const displayName = userProfile?.username || currentUser.displayName || currentUser.email?.split('@')[0] || 'User';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setCroppedImagePreviewUrl(null);
      setCrop(undefined);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImgSrc(reader.result as string);
        setIsCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };
  
  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(initialCrop);
  }

  async function getCroppedImg(image: HTMLImageElement, cropData: PixelCrop): Promise<string | null> {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = cropData.width;
    canvas.height = cropData.height;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = cropData.width * pixelRatio;
    canvas.height = cropData.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      cropData.x * scaleX,
      cropData.y * scaleY,
      cropData.width * scaleX,
      cropData.height * scaleY,
      0,
      0,
      cropData.width,
      cropData.height
    );
    
    return canvas.toDataURL('image/png');
  }

  const handleCropConfirm = async () => {
    if (completedCrop && imgRef.current) {
      const croppedDataUrl = await getCroppedImg(imgRef.current, completedCrop);
      setCroppedImagePreviewUrl(croppedDataUrl);
      setIsCropModalOpen(false);
    }
  };

  const handleUpload = async () => {
    let fileToUpload: File | null = null;

    if (croppedImagePreviewUrl && selectedFile) {
      fileToUpload = await dataURLtoFile(croppedImagePreviewUrl, selectedFile.name || 'profile.png');
    } else if (selectedFile) {
        console.warn("Uploading original file as no cropped image was available/confirmed.");
        fileToUpload = selectedFile;
    }

    if (!fileToUpload) {
      toast.error('No image to upload. Please select and crop an image.');
      return;
    }
    if (!auth.currentUser) {
      toast.error('User not authenticated.');
      return;
    }

    setUploading(true);
    try {
      const storageRef = ref(storage, `profilePictures/${auth.currentUser.uid}`);
      const snapshot = await uploadBytes(storageRef, fileToUpload);
      const downloadURL = await getDownloadURL(snapshot.ref);

      await updateProfile(auth.currentUser, { photoURL: downloadURL });
      if (auth.currentUser.email) {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userDocRef, { photoURL: downloadURL });
      }

      if (currentUser) {
        const updatedUser = { ...currentUser, photoURL: downloadURL };
        const updatedProfile = userProfile ? { ...userProfile, profilePictureUrl: downloadURL } : null;
        dispatch(setAuthData({ user: updatedUser, profile: updatedProfile, permissions }));
      }
      
      toast.success('Profile picture updated successfully');
      setCroppedImagePreviewUrl(null);
      setSelectedFile(null);
      setImgSrc(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

    } catch (error) {
      console.error('Error uploading profile picture:', error);
      if (window.location.hostname === 'localhost' && fileToUpload) {
          const reader = new FileReader();
          reader.onloadend = async () => {
            try {
              const localURL = reader.result as string;
              if (auth.currentUser) {
                await updateProfile(auth.currentUser, { photoURL: localURL });
                if (auth.currentUser.email) {
                   const userDocRef = doc(db, 'users', auth.currentUser.uid);
                   await updateDoc(userDocRef, { photoURL: localURL });
                }
                if (currentUser) {
                  const updatedUser = { ...currentUser, photoURL: localURL };
                  const updatedProfile = userProfile ? { ...userProfile, profilePictureUrl: localURL } : null;
                  dispatch(setAuthData({ user: updatedUser, profile: updatedProfile, permissions }));
                }
                toast.success('Profile picture updated successfully (local mode)');
              }
            } catch (localError) {
              toast.error('Failed to update profile with local image (dev mode).');
            }
          };
          reader.readAsDataURL(fileToUpload);
      } else {
        toast.error('Failed to upload profile picture. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const clearCookies = () => {
    try {
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      toast.success("Cookies cleared successfully");
    } catch (error) {
      console.error("Error clearing cookies:", error);
      toast.error("Failed to clear cookies");
    }
  };

  const clearSessionData = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      toast.success("Session data cleared successfully");
      await signOut(auth);
      toast.success("User signed out successfully");
      onClose();
    } catch (error) {
      console.error("Error clearing session data or signing out:", error);
      toast.error("Failed to clear session data or sign out");
    }
  };

  const clearCache = async (): Promise<void> => {
    try {
      if ('caches' in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map(name => window.caches.delete(name)));
        toast.success("Cache cleared successfully");
      } else {
        toast.info("Cache API not supported in this browser");
      }
    } catch (error) {
      console.error("Error clearing cache:", error);
      toast.error("Failed to clear cache");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('User signed out successfully');
      onClose();
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out. Please try again.');
    }
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side={side} className="sm:max-w-md w-[90vw]">
          <SheetHeader>
            <SheetTitle>Edit Profile</SheetTitle>
            <SheetDescription>
              Make changes to your profile and app experience here.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-2 px-6 mt-0">
            <div className="flex flex-col sm:flex-row gap-6 sm:items-start">
              <div className="flex flex-col items-center space-y-3 sm:w-1/3 pt-3">
                <div className="relative">
                  <Avatar className="h-24 w-24 border-2 border-primary/20">
                    <AvatarImage src={croppedImagePreviewUrl || currentUser.photoURL || undefined} alt={displayName} />
                    <AvatarFallback className="text-xl">{getInitials()}</AvatarFallback>
                  </Avatar>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="absolute bottom-0 right-0 rounded-full bg-background shadow-md"
                    onClick={triggerFileInput}
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                </div>
                <Input 
                  id="picture" 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
                
                {croppedImagePreviewUrl && (
                  <Button 
                    onClick={handleUpload} 
                    disabled={uploading} 
                    className="w-full mt-2"
                    variant="default"
                  >
                    {uploading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</>
                    ) : (
                      'Confirm Upload'
                    )}
                  </Button>
                )}
              </div>

              <div className="space-y-4 sm:w-2/3 sm:self-center">              
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm font-medium">{currentUser.email}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Display Name</Label>
                  <p className="text-sm font-medium">{displayName}</p>
                </div>
              </div>
            </div>

            <Separator />
            <div className="space-y-4"><ThemeToggle /></div>
            <Separator />
            <div className="space-y-4">
        <h3 className="text-sm font-medium">Privacy</h3>
        <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="w-full flex items-center justify-start" onClick={clearCookies}>
            <Cookie className="h-4 w-4 mr-2" /> Clear cookies
            </Button>
            <Button variant="outline" className="w-full flex items-center justify-start" onClick={clearSessionData}>
            <Loader2 className="h-4 w-4 mr-2" /> Clear session data
            </Button>
            <Button variant="outline" className="w-full flex items-center justify-start" onClick={clearCache}>
            <RefreshCw className="h-4 w-4 mr-2" /> Clear cache
            </Button>
        </div>
        </div>
        <Separator />
        <div className="mt-6">
        <Button variant="destructive" className="w-full flex items-center justify-center" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
        </Button>
        </div>

          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={isCropModalOpen} onOpenChange={setIsCropModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crop Profile Picture</DialogTitle>
          </DialogHeader>
          {imgSrc && (
            <div className="flex justify-center items-center">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                minWidth={50}
                minHeight={50}
                circularCrop={true}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  style={{ maxHeight: '70vh' }}
                />
              </ReactCrop>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={() => {
                setImgSrc(null);
                if(fileInputRef.current) fileInputRef.current.value = '';
              }}>Cancel</Button>
            </DialogClose>
            <Button onClick={handleCropConfirm} disabled={!completedCrop}>Confirm Crop</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileSheet; 